import React, { useEffect, useState } from 'react';
import { Dropdown, Button, Space, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { BranchesType } from 'services/api/type/branch.type';
import 'styles/branches-dropdown.scss';
import { setBranch, getBranch } from 'services/storages/userStorage';
import { useGetAllBranches } from 'hooks/useGetAllBranches';
import { useTranslation } from 'react-i18next';

const displayBranchName = (name: string) =>
  name?.replace(/\s*\(ARCHIVED\)\s*/gi, '').trim() ?? name;

const isArchivedBranch = (name: string) => /\(ARCHIVED\)/i.test(name ?? '');

// Function to generate menu items
const generateMenuItems = (
  branches: BranchesType[],
  loading: boolean,
  handleMenuClick: (branch: BranchesType) => void
): MenuProps['items'] => {
  if (loading) {
    return [
      {
        label: <Spin size="small" />,
        key: 'loading'
      }
    ];
  }
  return branches.map((branch, index) => ({
    label: (
      <div
        onClick={() => handleMenuClick(branch)}
        style={{ width: '100%', cursor: 'pointer', padding: '4px 0' }}
      >
        {index + 1} - {displayBranchName(branch.name)}
      </div>
    ),
    key: branch.id
  }));
};

const BranchesDropdown: React.FC = () => {
  const { t } = useTranslation();
  const { data: branches, isLoading: loading } = useGetAllBranches();
  const [selectedBranch, setSelectedBranch] = useState<BranchesType | null>(null);

  const visibleBranches = React.useMemo(
    () => (branches ?? []).filter(b => !isArchivedBranch(b.name)),
    [branches]
  );

  useEffect(() => {
    if (visibleBranches.length > 0) {
      const storedBranchId = getBranch();
      const branch = visibleBranches.find(b => b.id === storedBranchId) || visibleBranches[0];
      setSelectedBranch(branch);
      setBranch(branch.id);
    }
  }, [visibleBranches]);

  const handleMenuClick = (branch: BranchesType) => {
    setSelectedBranch(branch);
    setBranch(branch.id);
    window.location.reload();
  };

  const items = generateMenuItems(visibleBranches, loading, handleMenuClick);

  const selectedBranchIndex = selectedBranch
    ? (visibleBranches.findIndex(b => b.id === selectedBranch.id) ?? -1) + 1
    : 0;

  return (
    <Dropdown menu={{ items }} trigger={['click']} className="customDropdown">
      <Button className="customButton">
        <Space>
          {selectedBranch
            ? `${selectedBranchIndex} - ${displayBranchName(selectedBranch.name)}`
            : t('common.selectBranch')}
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );
};

export default BranchesDropdown;
